import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { isSessionKind, type SessionKind } from "@loom/shared/session";

import { httpDelete, httpGet, httpPatch } from "../lib/http";

// Wire schema for the Phase 9 sessions-index entries returned by
// `GET /sessions`. The legacy-fallback path (no index store wired) emits
// the same shape with empty modesUsed and the truncated first message
// as the title.
export const SessionIndexEntrySchema = z.object({
  sessionId: z.string(),
  projectId: z.number(),
  projectPath: z.string().optional(),
  title: z.string(),
  createdAt: z.string(),
  lastActiveAt: z.string(),
  status: z.enum(["active", "idle", "discarded"]),
  modesUsed: z.array(z.enum(["pm", "coding"])).optional(),
  branchName: z.string().nullable().optional(),
  hasOpenMr: z.boolean().optional(),
  mrUrl: z.string().nullable().optional(),
  live: z.boolean(),
  // The label of the most recent flow the conversation was bound to, written
  // server-side at the flow boundary and at implement-create. `.optional()` on
  // all three so a client running against an older server degrades to the
  // defaults below instead of failing the whole list parse.
  issueIid: z.number().nullable().optional(),
  issueTitle: z.string().nullable().optional(),
  // A warm sandbox: `bridgeState === "ready"` on the server. Distinct from
  // `live`, which is supervisor residency and outlives the pipeline it was
  // created for.
  warm: z.boolean().optional(),
  // What REOPENING this row gives you — the server's own hard answer, written
  // on the create INSERT and promoted monotonically on each user turn
  // (`unknown` → `chat` / `issue`, `implement` at create). List-only: the
  // detail route carries no kind and routes on its derived `flowIssueIid`.
  //
  // Parsed as a bare string and narrowed by the shared `isSessionKind` rather
  // than pinned to a `z.enum` of the four literals. Both halves matter: the
  // literals cannot drift from `SESSION_KIND_VALUES` because they are never
  // written here, and a value this bundle does not know — a server ahead of a
  // cached client — degrades that ONE row to `unknown` instead of throwing the
  // whole list parse and blanking the dashboard sidebar and the resume banner
  // along with the rail. `unknown` is the fail-closed answer: the rail lists
  // `chat` and withholds everything else.
  kind: z.string().optional(),
});

export interface SessionIndexEntry {
  readonly sessionId: string;
  readonly projectId: number;
  readonly projectPath: string;
  readonly title: string;
  readonly createdAt: string;
  readonly lastActiveAt: string;
  readonly status: "active" | "idle" | "discarded";
  readonly modesUsed: ReadonlyArray<"pm" | "coding">;
  readonly branchName: string | null;
  readonly hasOpenMr: boolean;
  readonly mrUrl: string | null;
  readonly live: boolean;
  readonly issueIid: number | null;
  readonly issueTitle: string | null;
  readonly warm: boolean;
  readonly kind: SessionKind;
}

const normalizeEntry = (
  raw: z.infer<typeof SessionIndexEntrySchema>,
): SessionIndexEntry => ({
  sessionId: raw.sessionId,
  projectId: raw.projectId,
  projectPath: raw.projectPath ?? "",
  title: raw.title,
  createdAt: raw.createdAt,
  lastActiveAt: raw.lastActiveAt,
  status: raw.status,
  modesUsed: raw.modesUsed ?? [],
  branchName: raw.branchName ?? null,
  hasOpenMr: raw.hasOpenMr ?? false,
  mrUrl: raw.mrUrl ?? null,
  live: raw.live,
  issueIid: raw.issueIid ?? null,
  issueTitle: raw.issueTitle ?? null,
  warm: raw.warm ?? false,
  kind: isSessionKind(raw.kind) ? raw.kind : "unknown",
});

const ListResponseSchema = z.object({
  sessions: z.array(SessionIndexEntrySchema),
});

const RenameResponseSchema = z.object({
  sessionId: z.string(),
  title: z.string(),
});

interface UseSessionsArgs {
  /** When set, the hook lists sessions for this project only. */
  readonly projectId?: number | undefined;
  /** When true, the hook fetches global recents instead. */
  readonly recent?: boolean;
  /** Default 20, max 100. */
  readonly limit?: number;
  /**
   * Ask the server to list only conversations it can still OPEN — rows whose
   * transcript survives in `app.session_history`. Opt-in: only the Focus
   * history rail passes it, because only there is every row a "reopen this"
   * button. The dashboard `SessionSidebar` and `ResumeBanner` deliberately keep
   * the unfiltered list.
   */
  readonly resumableOnly?: boolean;
  /**
   * Ask the server to list only conversations of one kind. Opt-in, exactly like
   * `resumableOnly`: the Focus history rail passes `"chat"` because reopening
   * an issue conversation is withheld there, and the dashboard `SessionSidebar`
   * / `ResumeBanner` / the threads accordion pass nothing and keep the whole
   * list. The server applies it in the same WHERE as the limit, so a full page
   * means a full page OF THAT KIND.
   */
  readonly kind?: "chat";
  /** Re-fetch whenever this counter increments. */
  readonly refreshKey?: number;
}

type State =
  | { kind: "loading" }
  | { kind: "ready"; sessions: SessionIndexEntry[] }
  | { kind: "error"; message: string };

const buildQuery = (args: UseSessionsArgs): string => {
  const params = new URLSearchParams();
  if (args.recent === true) params.set("recent", "true");
  if (args.projectId !== undefined) params.set("projectId", String(args.projectId));
  if (args.limit !== undefined) params.set("limit", String(args.limit));
  if (args.resumableOnly === true) params.set("resumableOnly", "true");
  if (args.kind !== undefined) params.set("kind", args.kind);
  return params.toString();
};

export const useSessions = (args: UseSessionsArgs) => {
  const [state, setState] = useState<State>({ kind: "loading" });

  const query = buildQuery(args);

  const reload = useCallback(() => {
    setState({ kind: "loading" });
    httpGet(`/sessions${query ? `?${query}` : ""}`, ListResponseSchema)
      .then(({ sessions }) =>
        setState({ kind: "ready", sessions: sessions.map(normalizeEntry) }),
      )
      .catch((err: Error) => setState({ kind: "error", message: err.message }));
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    httpGet(`/sessions${query ? `?${query}` : ""}`, ListResponseSchema)
      .then(({ sessions }) => {
        if (!cancelled)
          setState({ kind: "ready", sessions: sessions.map(normalizeEntry) });
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [query, args.refreshKey]);

  return { state, reload };
};

export const renameSession = (
  sessionId: string,
  title: string,
): Promise<z.infer<typeof RenameResponseSchema>> =>
  httpPatch(`/sessions/${sessionId}`, { title }, RenameResponseSchema);

export const deleteSession = (sessionId: string): Promise<void> =>
  httpDelete(`/sessions/${sessionId}`);

/**
 * `GET /sessions/:id` detail — the SINGLE model of that route's wire shape.
 *
 * `implement` is what both resume decisions turn on: when present, the session
 * is an autonomous run and must reopen on the ImplementFlow surface (re-seeded
 * from the persisted issue) rather than the generic chat. The per-project chat
 * page reads it through `useSessionImplement` below; the Focus history rail
 * `.extend()`s this schema with the two fields only IT needs (`endedAt`,
 * `flowIssueIid`), so the shared block has exactly one definition and a change
 * to the route reaches both surfaces at once.
 *
 * `.passthrough()` keeps the endpoint's other fields tolerated.
 */
export const SessionDetailSchema = z
  .object({
    sessionId: z.string(),
    implement: z
      .object({
        issueIid: z.number(),
        issueTitle: z.string(),
        issueWebUrl: z.string().nullable(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

/**
 * The project a session belongs to, read off the same `GET /sessions/:id` —
 * for a chat surface that was not handed one. `undefined` while it loads, when
 * the read fails, and whenever `sessionId` is `undefined` (the caller passes
 * that until it needs the answer, so a chat that never needs it costs no
 * request). Today's one reader: `ChatPage`, which checks a green milestone on
 * an inline card against the project's list (`milestoneListProjectId`).
 */
export const useSessionProjectId = (
  sessionId: string | undefined,
): number | undefined => {
  const [state, setState] = useState<{
    readonly sessionId: string;
    readonly projectId: number;
  } | null>(null);
  useEffect(() => {
    if (sessionId === undefined) return;
    let cancelled = false;
    httpGet(
      `/sessions/${sessionId}`,
      SessionDetailSchema.extend({ projectId: z.number() }),
    )
      .then((detail) => {
        if (!cancelled) setState({ sessionId, projectId: detail.projectId });
      })
      .catch(() => {
        // Unknown: the card then shows the value as it does while a list
        // loads — the same `unknown` the check itself has.
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);
  return state !== null && state.sessionId === sessionId ? state.projectId : undefined;
};

export interface SessionImplementDetail {
  readonly issueIid: number;
  readonly issueTitle: string;
  readonly issueWebUrl: string | null;
}

type ImplementDetailState =
  | { readonly loading: true; readonly implement: null }
  | { readonly loading: false; readonly implement: SessionImplementDetail | null };

/**
 * Resolve whether an active session is an autonomous implement run (and its
 * issue identity). Drives the per-project chat page's decision to mount
 * ImplementFlow (resume) vs ChatPage. A failed/absent fetch resolves to "not an
 * implement run" so the page falls back to the chat surface rather than hanging.
 */
export const useSessionImplement = (
  sessionId: string | undefined,
): ImplementDetailState => {
  const [state, setState] = useState<ImplementDetailState>(
    sessionId === undefined
      ? { loading: false, implement: null }
      : { loading: true, implement: null },
  );

  useEffect(() => {
    if (sessionId === undefined) {
      setState({ loading: false, implement: null });
      return;
    }
    let cancelled = false;
    setState({ loading: true, implement: null });
    httpGet(`/sessions/${sessionId}`, SessionDetailSchema)
      .then((detail) => {
        if (!cancelled) {
          setState({ loading: false, implement: detail.implement ?? null });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, implement: null });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return state;
};
